import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useContactViewModel } from "../../../viewmodels/website/useContactViewModel";

import ContactViewModal from "./components/ContactViewModal";

const ContactPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    contacts,
    loading,
    page,
    setPage,
    search,
    setSearch,
    deleteContact,
    markContactAsRead,
  } = useContactViewModel();

  const [openView, setOpenView] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  // ============================================================
  // INITIALIZE FROM URL
  // ============================================================

  useEffect(() => {
    setPage(Number(searchParams.get("page") ?? "1"));
    setSearch(searchParams.get("search") ?? "");
  }, []);

  // ============================================================
  // PAGINATION
  // ============================================================

  const changePage = (newPage: number) => {
    if (newPage < 1) {
      return;
    }

    setPage(newPage);

    setSearchParams({
      page: String(newPage),
      search,
    });
  };

  // ============================================================
  // VIEW CONTACT
  // ============================================================

  const handleView = async (id: string) => {
    // Mark as read first.
    // ViewModel will also update the local contact list
    // and unread count.
    await markContactAsRead(id);

    setSelectedId(id);
    setOpenView(true);
  };

  // ============================================================
  // DELETE CONTACT
  // ============================================================

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Contact?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) {
      return;
    }

    const success = await deleteContact(id);

    if (!success) {
      return;
    }

    await Swal.fire({
      icon: "success",
      title: "Deleted",
      timer: 1200,
      showConfirmButton: false,
    });
  };

  // ============================================================
  // SEARCH
  // ============================================================

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);

    setSearchParams({
      page: "1",
      search: value,
    });
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
            Requests
          </h1>

          <p className="text-gray-500 mt-1">
            Manage website requests
          </p>
        </div>

        {/* =====================================================
            SEARCH
        ====================================================== */}

        <div className="bg-white rounded-xl shadow border border-slate-300 p-4 mb-5">
          <label className="block text-sm font-medium mb-2">
            Search
          </label>

          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search..."
            className="w-full border border-slate-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* =====================================================
            TABLE
        ====================================================== */}

        <div className="bg-white rounded-2xl border overflow-hidden">

          <div className="overflow-x-auto">
            <table className="w-full text-sm">

              <thead className="bg-slate-100">
                <tr>
                  <th className="p-3 border text-left">
                    Name
                  </th>

                  <th className="p-3 border text-left">
                    Email
                  </th>

                  <th className="p-3 border text-left">
                    Phone
                  </th>

                  <th className="p-3 border text-left">
                    Status
                  </th>

                  <th className="p-3 border text-left">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>

                {/* LOADING */}

                {loading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="p-8 text-center text-slate-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (

                  /* NO DATA */

                  <tr>
                    <td
                      colSpan={5}
                      className="p-8 text-center text-slate-500"
                    >
                      No requests found
                    </td>
                  </tr>

                ) : (

                  /* DATA */

                  contacts.map((x: any) => (
                    <tr
                      key={x.id}
                      className="hover:bg-slate-50 transition"
                    >

                      {/* NAME */}

                      <td className="border p-3">
                        <div className="font-medium text-slate-800">
                          {x.firstName} {x.lastName}
                        </div>
                      </td>

                      {/* EMAIL */}

                      <td className="border p-3">
                        {x.email}
                      </td>

                      {/* PHONE */}

                      <td className="border p-3">
                        {x.phoneNumber}
                      </td>

                      {/* STATUS */}

                      <td className="border p-3">

                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-white text-xs font-medium ${
                            x.isRead
                              ? "bg-green-500"
                              : "bg-red-500"
                          }`}
                        >
                          {x.isRead ? "Read" : "Unread"}
                        </span>

                      </td>

                      {/* ACTIONS */}

                      <td className="border p-3">

                        <div className="flex gap-2">

                          {/* VIEW */}

                          <button
                            onClick={() => handleView(x.id)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg transition"
                          >
                            View
                          </button>

                          {/* DELETE */}

                          <button
                            onClick={() => handleDelete(x.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg transition"
                          >
                            Delete
                          </button>

                        </div>

                      </td>

                    </tr>
                  ))
                )}

              </tbody>
            </table>
          </div>
        </div>

        {/* =====================================================
            PAGINATION
        ====================================================== */}

        <div className="flex items-center justify-between mt-5">

          <button
            disabled={page === 1 || loading}
            onClick={() => changePage(page - 1)}
            className="border rounded-xl px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            Prev
          </button>

          <span className="text-sm text-slate-600">
            Page {page}
          </span>

          <button
            disabled={loading}
            onClick={() => changePage(page + 1)}
            className="border rounded-xl px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            Next
          </button>

        </div>

      </div>

      {/* =======================================================
          CONTACT VIEW MODAL
      ======================================================== */}

      <ContactViewModal
        isOpen={openView}
        contactId={selectedId}
        onClose={() => {
          setOpenView(false);
          setSelectedId("");
        }}
      />

    </DashboardLayout>
  );
};

export default ContactPage;